// One card per AI proposal: kind, title, detail rows. Approve (primary) does it; Ignore (outlined) clears the
// card everywhere without doing it. To change it, say or type what to change; the same card updates in place
// ("Updated"). Cards that settle a team check (review, confirm) have no Ignore: the question needs an answer.
// Used by the Assistant, Requests, Notifications and New activity, all reading the same list. Approve and Ignore
// are optimistic (lib/decide): the card resolves at once, and rolls back if the server says no.
//
// An email the assistant could not place (log_email) has Log it (pick the space and track by hand; it then becomes a
// normal card to approve) and Dismiss instead of Approve and Ignore.
//
// Variants: card (a full card with its rows), detail (Requests: the change box is on the card), compact (a context
// suggestion inside its group: one line of where, the text, small buttons), chat (in a conversation, the assistant
// panel and the Assistant page, Kareem 4 Nov: one row with the kind's icon, the title and the key detail, Approve and
// Change; Change opens the whole card; once decided it is one line, "Reminder Tomorrow 15:56, approved").
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { ApiError, PLACE_FIRST, type ActionKind, type ActionStatus, type ProposedAction } from '@dpw/shared';
import { api } from '../api';
import { useChange, useMe } from '../lib/queries';
import { useCardChange, useDecide } from '../lib/decide';
import { duration, ease, spring } from '../lib/motion';
import { ago } from '../lib/format';
import { ErrorNote } from './bits';
import { PlacePicker } from './email';
import { DrawnCheck, Orb } from './motion';
import { IconCalendar, IconChat, IconCheck, IconClock, IconDoc, IconFolder, IconMail, IconTrack, IconX } from './icons';

/** Kinds that settle a team check: Ignore would leave the question unanswered. Matches the server rule. */
export const NOT_IGNORABLE: ReadonlySet<ActionKind> = new Set(['review_answer', 'confirm_answer']);

export const STATUS_WORD: Record<ActionStatus, string> = { waiting: 'Waiting', approved: 'Approved', ignored: 'Ignored', rejected: 'Closed' };

const row = (a: ProposedAction, label: string) => a.rows.find(r => r.label === label)?.value ?? null;

/** What Approve says on this card ("Approve and log" for a forwarded email). */
export const approveLabel = (a: ProposedAction) => (a.source === 'email' || a.source === 'logged_email' ? 'Approve and log' : 'Approve');

export function ApproveCard({ action, onApproved, variant = 'card', composer = false, pickers }: {
  action: ProposedAction; onApproved?: (a: ProposedAction) => void; variant?: 'card' | 'detail' | 'compact' | 'chat';
  /** In a conversation: changes are typed in the composer below, so the card only says so. */
  composer?: boolean;
  /** Detail only: choices shown at the top of the proposal (a forwarded email's Space and track, Log as). */
  pickers?: ReactNode;
}) {
  const [text, setText] = useState('');
  const [placing, setPlacing] = useState(false);
  // A chat card opened with Change (its rows, Ignore, the change box).
  const [open, setOpen] = useState(false);
  const me = useMe();
  const decide = useDecide();
  const change = useCardChange((t: string) => api.actions.change(action.id, t));
  const approveDirect = useChange(() => api.actions.approve(action.id, action.version));
  const pick = useChange((answerId: string) => {
    if (action.kind !== 'confirm_answer' && action.kind !== 'review_answer') throw new Error('Not an answer card');
    return api.checks.confirm(action.payload.checkId, { answerId });
  });
  const deciding = decide.busy(action.id);
  const busy = deciding || change.isPending || pick.isPending || approveDirect.isPending;
  const err = change.error ?? pick.error ?? approveDirect.error;
  const done = action.status !== 'waiting';
  const link = typeof action.result?.link === 'string' ? action.result.link : null;
  const ignorable = !NOT_IGNORABLE.has(action.kind);

  // New activity needs the approved card back (to open what it made), so it waits for the server.
  const approve = () => (onApproved ? approveDirect.mutate(undefined, { onSuccess: a => onApproved(a) }) : decide.approve(action));
  const ignore = () => decide.ignore(action);

  const submitChange = (e: FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    if (/^approve[.!]?$/i.test(t)) { approve(); setText(''); return; }
    if (ignorable && /^ignore( it| this)?[.!]?$/i.test(t)) { ignore(); setText(''); return; }
    change.mutate(t, { onSuccess: () => setText('') });
  };
  const answers = (action.kind === 'confirm_answer' || action.kind === 'review_answer') ? action.payload.answers : [];
  const stillWaiting = action.kind === 'review_answer' ? action.payload.stillWaiting : 0;
  // A refused context replace sends the section as it is now: show it, so the change can be made against it.
  const cur = err instanceof ApiError && err.current && typeof err.current === 'object' ? (err.current as { text?: unknown }) : null;
  const nowText = cur && typeof cur.text === 'string' ? cur.text : null;
  const isAnswerCard = action.kind === 'confirm_answer' || action.kind === 'review_answer';
  const chosenId = isAnswerCard ? action.payload.chosenAnswerId ?? null : null;
  const needsPick = isAnswerCard && answers.length > 1 && !chosenId && !action.payload.combinedText;
  const forOther = me.data && action.owner.id !== me.data.id;
  const toPlace = PLACE_FIRST.has(action.kind);
  // A compact card is usually a context suggestion ("Added to ..."); other kinds shown compact (what Talk captured, say a
  // reminder) say Approved like any card.
  const suggestion = variant === 'compact' && action.kind === 'update_context';

  const changeBox = (
    <form className={variant === 'compact' ? 'askmini sm' : 'aibar chg'} onSubmit={submitChange}>
      <input value={text} onChange={e => setText(e.target.value)} placeholder="Or say or type what to change" aria-label="What to change" disabled={busy} data-change />
      {change.isPending && <span className="spin" style={{ marginRight: 8 }} />}
    </form>
  );

  const resolved = (
    <motion.div className="resolved" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0, transition: spring.gentle }}>
      {action.status === 'approved'
        ? <span className="apd"><DrawnCheck />{suggestion ? `Added to ${row(action, 'Context') ?? 'the context'}.` : `Approved${action.decidedAt ? ` ${ago(action.decidedAt)}` : ''}`}{link && variant !== 'compact' && <> · <Link className="link" to={link}>Open</Link></>}</span>
        : <span className="xs mut">{action.status === 'ignored' ? (suggestion ? 'Ignored. Nothing was added.' : toPlace ? 'Dismissed. Nothing was logged.' : 'Ignored. Nothing was done.') : STATUS_WORD[action.status]}</span>}
      {action.result?.note && variant !== 'compact' ? <span className="xs mut">{String(action.result.note)}</span> : null}
    </motion.div>
  );

  const buttons = toPlace ? (
    <>
      <button className="bt pri" type="button" disabled={busy} aria-expanded={placing} onClick={() => setPlacing(true)} data-logit>Log it</button>
      <button className="bt" type="button" disabled={busy} onClick={ignore} data-ignore>Dismiss</button>
    </>
  ) : (
    <>
      <button className={`bt pri${variant === 'compact' ? ' sm' : ''}`} type="button" disabled={busy || needsPick} data-approve
        title={needsPick ? 'Pick which answer is right, or write the right answer, first' : undefined} onClick={approve}>
        {approveDirect.isPending ? <span className="spin" /> : null}{variant === 'detail' ? approveLabel(action) : 'Approve'}
      </button>
      {ignorable && <button className={`bt${variant === 'compact' ? ' sm' : ''}`} type="button" disabled={busy} onClick={ignore} data-ignore>Ignore</button>}
    </>
  );

  // The chat card, one row. Cards that need a choice first (an answer to pick, an email to place) stay whole.
  if (variant === 'chat' && !open && !toPlace && !answers.length && nowText === null) {
    const detail = chatDetail(action);
    if (done) {
      const word = action.status === 'approved' ? 'approved' : action.status === 'ignored' ? 'ignored, nothing was done' : STATUS_WORD[action.status].toLowerCase();
      return (
        <motion.div className="acard chat done" data-card={action.id} data-kind={action.kind} initial={{ opacity: 0.6 }} animate={{ opacity: 1, transition: spring.gentle }}>
          <span className="ci" aria-hidden="true">{action.status === 'approved' ? <DrawnCheck /> : <KindIcon kind={action.kind} />}</span>
          <span className="cl">{action.kindLabel}{detail.when ? ` ${detail.when}` : ''}, {word}</span>
          {link && action.status === 'approved' && <Link className="link xs" to={link}>Open</Link>}
        </motion.div>
      );
    }
    return (
      <div className="acard chat" data-card={action.id} data-kind={action.kind}>
        <div className="crow">
          <span className="ci" aria-hidden="true"><KindIcon kind={action.kind} /></span>
          <span className="cb">
            <span className="ct" title={action.title}>{action.title}{action.updated && <span className="lbl upd">Updated</span>}</span>
            <span className="cd">{[action.kindLabel, detail.when, detail.where, forOther ? `for ${action.owner.name}` : null].filter(Boolean).join(' · ')}</span>
          </span>
          <span className="cbt">
            <button className="bt pri sm" type="button" disabled={busy} data-approve onClick={approve}>{approveDirect.isPending ? <span className="spin" /> : null}Approve</button>
            <button className="bt sm" type="button" aria-expanded={false} onClick={() => setOpen(true)}>Change</button>
            {/* Every Approve has an Ignore next to it (the rule on every card): here a small one. */}
            {ignorable && <button className="bt sm cx" type="button" disabled={busy} onClick={ignore} data-ignore title="Ignore"><IconX /><span className="vh">Ignore</span></button>}
          </span>
        </div>
        {err ? <ErrorNote error={err} /> : null}
      </div>
    );
  }

  if (variant === 'compact') {
    const adds = row(action, 'Adds') ?? row(action, 'Replaces it with') ?? '';
    const basis = action.rows.find(r => !['Asked by', 'Context', 'Section', 'Adds', 'Replaces it with', 'Now'].includes(r.label));
    return (
      <div className={`cg${done ? ' done' : ''}`} data-card={action.id} data-kind={action.kind}>
        <div className="cgk">{action.sourceLabel}{basis ? `: ${basis.value}` : ''} · {ago(action.createdAt)}{forOther ? ` · for ${action.owner.name}` : ''}</div>
        <div className="cgt">{action.title}{action.updated && !done && <span className="lbl upd">Updated</span>}</div>
        {/* Where it goes, on its own line under the title (never split across the title's last line). */}
        <div className="cgm">{row(action, 'Context') ?? action.spaceName}</div>
        <div className="cgq">{adds}</div>
        <AnimatePresence mode="wait" initial={false}>
          {done ? <motion.div key="r">{resolved}</motion.div> : (
            <motion.div key="b" className="rowc" exit={{ opacity: 0, transition: { duration: duration.fast, ease } }}>{buttons}{changeBox}</motion.div>
          )}
        </AnimatePresence>
        {err ? <ErrorNote error={err} /> : null}
        {nowText !== null && <div className="xs mut">Now, after the latest edit: {nowText || '(empty)'}</div>}
      </div>
    );
  }

  return (
    <div className={`acard ${variant === 'chat' ? 'card chat open' : variant}${done ? ' done' : ''}`} data-card={action.id} data-kind={action.kind}>
      {(variant === 'card' || variant === 'chat') && (
        <div className="ah">
          <span className="k">{action.kindLabel}</span>
          {action.updated && !done && <span className="lbl upd">Updated</span>}
          {forOther && <span className="lbl upd">For {action.owner.name}</span>}
          <span className="xs mut" style={{ marginLeft: 'auto' }}>{action.sourceLabel} · {ago(action.updatedAt)}{action.spaceName ? ` · ${action.spaceName}` : ''}</span>
          {variant === 'chat' && open && <button type="button" className="bt sm ghost" aria-expanded onClick={() => setOpen(false)}>Less</button>}
        </div>
      )}
      {(variant === 'card' || variant === 'chat') && <h4>{action.title}</h4>}
      {variant === 'detail' ? (
        <div className="propose">
          <div className="rowc gap6"><span className="k"><Orb size={9} />{toPlace ? 'The assistant could not place it' : 'The assistant proposes'}</span>{action.updated && !done && <span className="lbl upd">Updated</span>}</div>
          {!done && pickers}
          {action.rows.map((r, i) => <div className="pr" key={i}><span className="k2">{r.label}</span><span className="v">{r.value}</span></div>)}
        </div>
      ) : action.rows.length > 0 && (
        <div className="rows">
          {action.rows.map((r, i) => <div className="f" key={i}><span className="k2">{r.label}</span><span className="v">{r.value}</span></div>)}
        </div>
      )}
      {!done && toPlace && placing && <PlacePicker a={action} />}
      {!done && stillWaiting > 0 && <div className="note">{stillWaiting} more {stillWaiting === 1 ? 'person has' : 'people have'} not answered yet. More may come; this card updates when they do.</div>}
      {!done && answers.length > 1 && (
        <div className="rowc gap6"><span className="xs mut">This one is right:</span>
          {answers.map(a => <button key={a.answerId} type="button" className={`bt sm${chosenId === a.answerId ? ' on' : ''}`} aria-pressed={chosenId === a.answerId} disabled={busy} onClick={() => pick.mutate(a.answerId)}>{a.name.split(' ')[0]}</button>)}
        </div>
      )}
      <AnimatePresence mode="wait" initial={false}>
        {done ? <motion.div key="r" className="foot">{resolved}</motion.div> : (
          <motion.div key="b" className="foot" exit={{ opacity: 0, transition: { duration: duration.fast, ease } }}>
            {buttons}
            {toPlace ? null : composer && variant !== 'chat' ? <span className="xs mut hintch">Or say or type what to change</span> : changeBox}
          </motion.div>
        )}
      </AnimatePresence>
      {err ? <ErrorNote error={err} /> : null}
      {nowText !== null && (
        <div className="rows" aria-label="The section as it is now">
          <div className="f"><span className="k2">Now, after the latest edit</span><span className="v">{nowText || '(empty)'}</span></div>
        </div>
      )}
    </div>
  );
}

/** The key detail of a chat card: when (a reminder's time, a due date) and where (the space). */
function chatDetail(a: ProposedAction): { when: string | null; where: string | null } {
  const when = a.rows.find(r => r.label === 'When' || r.label === 'Due')?.value ?? null;
  return { when, where: a.spaceName ?? null };
}

/** A small icon for what a card does. */
export function KindIcon({ kind }: { kind: ActionKind }) {
  switch (kind) {
    case 'create_reminder': case 'check_in': return <IconClock />;
    case 'create_activity': case 'update_activity': return <IconCalendar />;
    case 'log_email': return <IconMail />;
    case 'send_message': case 'nudge_check': case 'confirm_answer': case 'review_answer': case 'set_member': case 'remove_member': return <IconChat />;
    case 'create_space': case 'create_track': case 'archive': return <IconTrack />;
    case 'add_file': case 'file_minutes': return <IconFolder />;
    case 'add_insight': case 'update_context': case 'start_research': case 'follow_link': case 'add_research_topic': return <IconDoc />;
    default: return <IconCheck />;
  }
}
